import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PurpleNavBar } from '../components/PurpleNavBar';
import { COLORS } from '../../../core/constants/colors';
import { api } from '../../../core/utils/apiClient';
import { adminFormsService } from '../../admin/forms/services/admin-forms.service';
import { FormSubmission } from '../../admin/forms/types';

export const PartnerFormSubmissionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [showSubmissionDrawer, setShowSubmissionDrawer] = useState(false);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const itemsPerPage = 10;

  useEffect(() => {
    loadPartnerCompany();
  }, []);

  useEffect(() => {
    if (companyId) {
      loadSubmissions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadPartnerCompany = async () => {
    try {
      // Get the partner's own first company (scoped, not the global portfolio)
      const response = await api.get('/api/v1/companies/?company_mode=true&limit=1');

      if (response.ok) {
        const data = await response.json();
        const companies = data.companies || data || [];

        if (companies.length > 0) {
          setCompanyId(companies[0].id);
        } else {
          setError('No company found. Please contact support.');
          setIsLoading(false);
        }
      } else {
        setError('Failed to load company data');
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('Error loading partner company:', err);
      setError(err.message || 'Failed to load company data');
      setIsLoading(false);
    }
  };

  const loadSubmissions = async () => {
    if (!companyId) return;

    setIsLoading(true);
    setError('');
    try {
      const data = await adminFormsService.getAllSubmissions({ company_id: companyId });
      setSubmissions(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredSubmissions = submissions.filter((submission) => {
    const matchesSearch = searchQuery === '' ||
      submission.id.toString().includes(searchQuery) ||
      submission.submitted_by?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const paginatedSubmissions = filteredSubmissions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleViewSubmission = (submission: FormSubmission) => {
    setSelectedSubmission(submission);
    setShowSubmissionDrawer(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
        <PurpleNavBar />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div
              className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mb-4"
              style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
            />
            <p style={{ color: COLORS.primary.persianIndigo }}>Loading submissions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F5', fontFamily: 'DM Sans, sans-serif' }}>
      <PurpleNavBar />

      {/* Header */}
      <div className="relative overflow-hidden" style={{ backgroundColor: COLORS.user.primary }}>
        <div className="absolute right-0 top-1/2 transform -translate-y-1/2 opacity-18 pointer-events-none">
          <img
            src="/new_logo_white_P.png"
            alt="Portfolio Indexing System"
            className="h-96 opacity-50"
          />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-8 py-12">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">
                Form Submissions
              </h1>
              <p className="text-white text-sm" style={{ opacity: 0.9 }}>
                View and manage your form submissions
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 rounded-lg font-medium text-white hover:bg-white hover:bg-opacity-20 transition-all"
              style={{ border: '1px solid rgba(255,255,255,0.3)' }}
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1400px] mx-auto px-8 py-8">
        {error && (
          <div className="mb-6 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
            <p className="text-sm" style={{ color: COLORS.extended.redSalsa }}>{error}</p>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search submissions..."
                  className="w-full px-4 py-3 pl-10 rounded-lg border-2 focus:outline-none focus:ring-2 text-sm"
                  style={{
                    borderColor: COLORS.extended.cultured,
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = COLORS.user.queryButton;
                    e.target.style.boxShadow = `0 0 0 2px ${COLORS.user.queryButton}40`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = COLORS.extended.cultured;
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5"
                  style={{ color: '#9CA3AF' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            <button
              onClick={loadSubmissions}
              className="px-4 py-3 rounded-lg font-medium text-sm transition-all"
              style={{ backgroundColor: COLORS.user.queryButton, color: 'white' }}
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Submissions Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {filteredSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 mb-4"
                style={{ color: '#9CA3AF' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-semibold mb-2" style={{ color: '#1F2937' }}>
                No submissions found
              </p>
              <p className="text-sm" style={{ color: '#6B7280' }}>
                {searchQuery ? 'Try adjusting your search query' : 'You haven\'t submitted any forms yet'}
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                        Submission ID
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                        Form Schema ID
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                        Submitted By
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                        Completeness Score
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                        Submitted At
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#6B7280' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedSubmissions.map((submission) => (
                      <tr key={submission.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm font-medium" style={{ color: '#1F2937' }}>
                            #{submission.id}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm" style={{ color: '#6B7280' }}>
                            {submission.form_schema_id}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm" style={{ color: '#6B7280' }}>
                            {submission.submitted_by || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {submission.completeness_score !== undefined ? (
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="h-2 rounded-full transition-all"
                                  style={{
                                    width: `${submission.completeness_score}%`,
                                    backgroundColor: submission.completeness_score >= 80 ? '#4CAF50' : submission.completeness_score >= 50 ? '#FF9800' : '#F44336',
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium" style={{ color: '#1F2937' }}>
                                {submission.completeness_score}%
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm" style={{ color: '#9CA3AF' }}>N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm" style={{ color: '#6B7280' }}>
                            {new Date(submission.submitted_at).toLocaleDateString()}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleViewSubmission(submission)}
                            className="px-4 py-2 rounded-lg text-sm font-medium transition-all"
                            style={{
                              backgroundColor: COLORS.user.primaryLight,
                              color: COLORS.user.primary
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = COLORS.user.primary;
                              e.currentTarget.style.color = 'white';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = COLORS.user.primaryLight;
                              e.currentTarget.style.color = COLORS.user.primary;
                            }}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t flex items-center justify-between" style={{ borderColor: '#E5E7EB' }}>
                  <div className="text-sm" style={{ color: '#6B7280' }}>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredSubmissions.length)} of {filteredSubmissions.length} submissions
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: currentPage === 1 ? '#F3F4F6' : COLORS.user.primaryLight,
                        color: currentPage === 1 ? '#9CA3AF' : COLORS.user.primary
                      }}
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 text-sm font-medium" style={{ color: '#1F2937' }}>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{
                        backgroundColor: currentPage === totalPages ? '#F3F4F6' : COLORS.user.primaryLight,
                        color: currentPage === totalPages ? '#9CA3AF' : COLORS.user.primary
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Submission Details Drawer */}
      {showSubmissionDrawer && selectedSubmission && (
        <SubmissionDrawer
          submission={selectedSubmission}
          submissionId={null}
          isOpen={showSubmissionDrawer}
          onClose={() => {
            setShowSubmissionDrawer(false);
            setSelectedSubmission(null);
          }}
        />
      )}
    </div>
  );
};

// Submission Drawer Component (simplified version)
interface SubmissionDrawerProps {
  submission: FormSubmission | null;
  submissionId: number | null;
  isOpen: boolean;
  onClose: () => void;
}

const SubmissionDrawer: React.FC<SubmissionDrawerProps> = ({
  submission: submissionProp,
  submissionId,
  isOpen,
  onClose,
}) => {
  const [submission, setSubmission] = useState<FormSubmission | null>(submissionProp || null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (submissionProp) {
        setSubmission(submissionProp);
      } else if (submissionId) {
        loadSubmission();
      }
    } else {
      setSubmission(null);
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, submissionId, submissionProp]);

  const loadSubmission = async () => {
    if (!submissionId) return;
    setIsLoading(true);
    setError('');
    try {
      const data = await adminFormsService.getSubmissionById(submissionId);
      if (data) {
        setSubmission(data);
      } else {
        setError('Submission not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load submission');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Side Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-3xl bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="px-6 py-5 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold" style={{ color: '#1F2937' }}>
              Submission Details
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 py-6 overflow-y-auto">
            {isLoading && (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-t-transparent mb-2"
                  style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
                />
                <p className="text-sm" style={{ color: '#6B7280' }}>Loading submission...</p>
              </div>
            )}

            {error && (
              <div className="mb-4 p-4 rounded-lg" style={{ backgroundColor: '#FEE2E2' }}>
                <p className="text-sm" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            {submission && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Submission ID
                    </label>
                    <p className="text-base font-semibold" style={{ color: '#1F2937' }}>
                      #{submission.id}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Company ID
                    </label>
                    <p className="text-base" style={{ color: '#1F2937' }}>
                      {submission.company_id}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Form Schema ID
                    </label>
                    <p className="text-base" style={{ color: '#1F2937' }}>
                      {submission.form_schema_id}
                    </p>
                  </div>
                  {submission.submitted_by && (
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                        Submitted By
                      </label>
                      <p className="text-base" style={{ color: '#1F2937' }}>
                        {submission.submitted_by}
                      </p>
                    </div>
                  )}
                </div>

                {submission.completeness_score !== undefined && (
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Completeness Score
                    </label>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-gray-200 rounded-full h-4">
                        <div
                          className="h-4 rounded-full transition-all"
                          style={{
                            width: `${submission.completeness_score}%`,
                            backgroundColor: submission.completeness_score >= 80 ? '#4CAF50' : submission.completeness_score >= 50 ? '#FF9800' : '#F44336',
                          }}
                        />
                      </div>
                      <span className="text-base font-semibold" style={{ color: '#1F2937' }}>
                        {submission.completeness_score}%
                      </span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                    Submission Data
                  </label>
                  <div className="border rounded-lg p-4 max-h-96 overflow-y-auto" style={{ borderColor: '#E5E7EB' }}>
                    {Object.keys(submission.submission_data).length === 0 ? (
                      <p className="text-sm" style={{ color: '#9CA3AF' }}>No submission data</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(submission.submission_data).map(([key, value]) => (
                          <div
                            key={key}
                            className="p-3 rounded-lg bg-gray-50"
                          >
                            <label className="text-xs font-medium uppercase mb-1 block" style={{ color: '#6B7280' }}>
                              {key}
                            </label>
                            <p className="text-sm" style={{ color: '#1F2937' }}>
                              {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 pt-4 border-t" style={{ borderColor: '#E5E7EB' }}>
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                      Submitted At
                    </label>
                    <p className="text-base" style={{ color: '#6B7280' }}>
                      {new Date(submission.submitted_at).toLocaleString()}
                    </p>
                  </div>
                  {submission.last_scored_at && (
                    <div>
                      <label className="block text-sm font-medium mb-2" style={{ color: '#6B7280' }}>
                        Last Scored At
                      </label>
                      <p className="text-base" style={{ color: '#6B7280' }}>
                        {new Date(submission.last_scored_at).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

